import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import Loader from "../components/Loader";
import { toCamelCase, formatCurrency, getAlfrescoVariantDefinition, formatName, formatPhoneNumber } from "../Utils/Helpers";
import { usePurchase } from '../context/PurchaseContext';
import AddressAutocomplete from '../components/AddressAutocomplete';
import Stripe from '../components/Stripe';
import Paypal from '../components/Paypal';
import affirmLogo from '../images/affirmLogo.png';

const CustomProductCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    userInfo, 
    setUserInfo, 
    orderData, 
    product, 
    setProduct, 
    quantity, 
    setQuantity,
    shippingCost,
    setShippingCost
  } = usePurchase();
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const [errors, setErrors] = useState({});
  const [enablePayment, setEnablePayments] = useState(false);
  const [popupCC, setPopupCC] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [discount, setDiscount] = useState(0);

  const calculateDeliveryFee = useCallback(() => {
    const fee = parseFloat(shippingCost) || 0;
    return fee;
  }, [shippingCost]);

  const updateOrderData = useCallback(() => {
    const subtotal = product.Price * quantity;
    const shipping = parseFloat(shippingCost) || 0;
    const total = subtotal + shipping;
    
    const currentOrderData = {
      name: formatName(userInfo.name, userInfo.lastName),
      address: userInfo.address,
      city: userInfo.city,
      state: userInfo.state,
      postalCode: userInfo.postalCode,
      country: userInfo.country,
      phone: formatPhoneNumber(userInfo.phone),
      email: userInfo.email,
      subtotal: formatCurrency(subtotal),
      shipping: {
        cost: formatCurrency(shipping),
        method: "Standard Shipping",
        estimatedDelivery: "3-5 business days"
      },
      total: formatCurrency(total),
      products: [{
        imageUrl: product.Image,
        title: product.Title,
        price: formatCurrency(product.Price),
        quantity: quantity
      }]
    };

  }, [userInfo, product, quantity, shippingCost]);

  useEffect(() => {
    if (location.state?.product) {
      setProduct(location.state.product);
      if (location.state.quantity) {
        setQuantity(location.state.quantity);
      }
    }
  }, [location.state]);

  const handleAddressSelect = (addressObject) => {
    setUserInfo((prevInfo) => ({
      ...prevInfo,
      address: addressObject.formatted_address,
      streetNumber: addressObject.streetNumber,
      city: addressObject.city,
      state: addressObject.state,
      postalCode: addressObject.postalCode,
      country: addressObject.country,
      hasSelectedAddress: true
    }));
    updateOrderData();
  };

  const calculateShippingCost = async () => {
    // If quantity is 1, return flat rate of $6.99
    if (quantity === 1) {
      const flatRate = 6.99;
      setShippingCost(flatRate);
      return flatRate;
    }

    // For multiple items, check if address was selected through Google Autocomplete
    if (!userInfo.hasSelectedAddress) {
      setShippingCost(0);
      return 0;
    }

    setIsCalculatingShipping(true);
    setShippingError(null);

    try {
      const response = await fetch('https://server-seven-rust.vercel.app/api/calculate-shipping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order: {
            from: {
              name: "Badass BBQs",
              street1: "325 Scuffleboro Rd",
              city: "Eatonton",
              state: "GA",
              zip: "31024",
              country: "US"
            },
            to: {
              name: `${userInfo.name} ${userInfo.lastName}`,
              street1: userInfo.address,
              city: userInfo.city,
              state: userInfo.state,
              zip: userInfo.postalCode,
              country: userInfo.country || "US"
            },
            parcel: {
              length: 5.75,
              width: 2.75,
              height: 5.75 * quantity,
              weight: 11.5 * quantity
            }
          },
          service: "Priority",
          carrier: "USPS"
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error('Failed to calculate shipping cost');
      }

      // Extract shipping rate from response[0].retail_rate and ensure it's a number
      const shippingRate = parseFloat(data[0]?.retail_rate) || 0;
      
      setShippingCost(shippingRate);
      return shippingRate;
    } catch (error) {
      setShippingError('Failed to calculate shipping cost. Please try again.');
      setShippingCost(0);
      return 0;
    } finally {
      setIsCalculatingShipping(false);
    }
  };

  useEffect(() => {
    // Only calculate shipping if address was selected through Google Autocomplete
    if (userInfo.hasSelectedAddress) {
      calculateShippingCost();
    } else {
      // Reset shipping cost if no address selected
      setShippingCost(quantity === 1 ? 6.99 : 0);
    }
  }, [
    userInfo.hasSelectedAddress,
    userInfo.address,
    userInfo.city,
    userInfo.state,
    userInfo.postalCode,
    userInfo.country,
    quantity
  ]);

  const handlePromoCode = () => {
    setPromoError('');
    if (promoCode.toUpperCase() === 'LOVERUB10') {
      setDiscount(0.10); // 10% discount
      setPromoError('');
    } else {
      setPromoError('Invalid promo code');
      setDiscount(0);
    }
  };

  const calculateSubtotal = () => {
    const subtotal = product.Price * quantity;
    return subtotal * (1 - discount);
  };

  const totalCost = calculateSubtotal() + calculateDeliveryFee();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
    updateOrderData();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10,15}$/;
    return phoneRegex.test(phone);
  };

  const validateForm = () => {
    let validationErrors = {};

    if (!userInfo.name) {
      validationErrors.name = "Please enter your first name.";
    }

    if (!userInfo.lastName) {
      validationErrors.lastName = "Please enter your last name.";
    }

    if (!userInfo.address) {
      validationErrors.address = "Please enter your address.";
    }

    if (!validateEmail(userInfo.email)) {
      validationErrors.email = "Please enter a valid email address.";
    }

    if (!validatePhone(userInfo.phone)) {
      validationErrors.phone = "Please enter a valid phone number (10-15 digits).";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handlePaymentClick = () => {
    if (validateForm()) {
      setEnablePayments(true);
      updateOrderData();
    }
  };

  const handleClose = (e) => {
    if (e.target.id === 'overlay') {
      setPopupCC(false);
    }
  };

  const payWithAffirm = () => {
    const itemsForAffirm = [
      {
        display_name: product.Title,
        sku: `${product.brand}-${product.Id}` || "N/A",
        unit_price: product.Price * 100,
        qty: quantity,
        item_image_url: product.Image,
        item_url: `https://bad-ass-bb-qs-ecommerce.vercel.app/product/${product.brand}-${product.Id}`,
        categories: product.Category,
      },
    ];

    window.affirm.checkout({
      merchant: {
        user_confirmation_url: `${process.env.REACT_APP_SERVER_URL}/api/confirm-order`,
        user_cancel_url: `${process.env.REACT_APP_SERVER_URL}/checkout-canceled-affirm`,
        public_api_key: process.env.REACT_APP_PUBLIC_API_AFFIRM,
        user_confirmation_url_action: "POST",
        name: "BadAssBBQs",
      },
      shipping: {
        name: {
          first: userInfo.name,
          last: userInfo.lastName,
        },
        address: {
          line1: userInfo.address,
          city: userInfo.city,
          state: userInfo.state,
          zipcode: userInfo.postalCode,
          country: userInfo.country,
        },
        phone_number: userInfo.phone,
        email: userInfo.email,
      },
      billing: {
        name: {
          first: userInfo.name,
          last: userInfo.lastName,
        },
        address: {
          line1: userInfo.address,
          city: userInfo.city,
          state: userInfo.state,
          zipcode: userInfo.postalCode,
          country: userInfo.country,
        },
        phone_number: userInfo.phone,
        email: userInfo.email,
      },
      items: itemsForAffirm,
      order_id: "SINGLEPROD-" + Date.now(),
      currency: "USD",
      shipping_amount: calculateDeliveryFee() * 100,
      tax_amount: 0,
      total: (product.Price * quantity + calculateDeliveryFee()) * 100,
    });
    window.affirm.checkout.open();
  };

  const getShippingDisplay = () => {
    if (quantity === 1) {
      return formatCurrency(6.99);
    }

    if (!userInfo.hasSelectedAddress) {
      return 'Please select a shipping address';
    }

    if (isCalculatingShipping) {
      return 'Calculating...';
    }

    if (shippingError) {
      return <span className="text-red-500">{shippingError}</span>;
    }

    const shipping = parseFloat(shippingCost) || 0;
    return formatCurrency(shipping);
  };

  // Update order data when quantity changes
  useEffect(() => {
    updateOrderData();
  }, [quantity, updateOrderData]);

  // Update order data when shipping cost changes
  useEffect(() => {
    updateOrderData();
  }, [shippingCost, updateOrderData]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex justify-center items-center mt-[10px] xl:mt-0 pt-[50px] mb-[80px]">
      <div className="bg-white p-[10px] md:p-[20px] rounded border border-gray-200 w-[90%]">
        <div className="flex flex-col sm:flex-row justify-between mb-[30px]">
          <p className="font-bold text-[30px] text-center sm:text-auto">Checkout</p>
          <p className="font-bold text-[30px] text-center sm:text-auto">
            {formatCurrency(totalCost)}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <div className="border border-gray-200 rounded p-[20px]">
            <div className="flex flex-col">
              <p className="font-semibold text-[20px] mb-[25px]">Contact Information:</p>
              <div className="mb-4 basis-[50%]">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={userInfo.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded outline-none"
                  placeholder="First Name"
                />
                {errors.name && <p className="text-red text-sm">{errors.name}</p>}
              </div>
              <div className="mb-4 basis-[50%]">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={userInfo.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded outline-none"
                  placeholder="Last Name"
                />
                {errors.lastName && <p className="text-red text-sm">{errors.lastName}</p>}
              </div>
            </div>

            <div className="mb-4">
              <input
                type="email"
                id="email"
                name="email"
                value={userInfo.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded outline-none"
                placeholder="Email Address"
              />
              {errors.email && <p className="text-red text-sm">{errors.email}</p>}
            </div>

            <div className="mb-4">
              <input
                type="tel"
                id="phone"
                name="phone"
                value={userInfo.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded outline-none"
                placeholder="Phone Number"
              />
              {errors.phone && <p className="text-red text-sm">{errors.phone}</p>}
            </div>

            <div className="mb-6">
              <AddressAutocomplete onAddressSelect={handleAddressSelect} />
              {errors.address && <p className="text-red text-sm">{errors.address}</p>}
            </div>

            <div className="mb-6">
              <p className="font-semibold text-[20px] mb-[10px]">Promo Code:</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Enter promo code"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded outline-none"
                />
                <button
                  onClick={handlePromoCode}
                  className="px-4 py-2 bg-red text-white rounded hover:bg-red-700 transition-colors"
                >
                  Apply
                </button>
              </div>
              {promoError && <p className="text-red text-sm mt-1">{promoError}</p>}
              {discount > 0 && (
                <p className="text-green-600 text-sm mt-1">
                  Promo code applied! 10% discount added.
                </p>
              )}
            </div>

            <div style={{ display: enablePayment ? 'block' : 'none' }}>
              <p className="font-semibold text-[20px] mb-[25px]">Payment Method:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[10px]">
                <p
                  className="h-[43px] bg-white text-center text-gray-800 border border-gray-500 rounded text-[15px] leading-[40px] cursor-pointer"
                  onClick={() => setPopupCC(true)}
                >
                  <i className="bi bi-credit-card-2-front"></i> Credit Card
                </p>
                <Paypal total={(product.Price * quantity) + calculateDeliveryFee()} className='cursor-not-allowed' />
                <div className="border border-gray-500 rounded px-[7px] h-[44px] w-full flex items-center justify-center">
                  <img src={affirmLogo} className='h-[100%] object-contain' onClick={payWithAffirm} alt="Affirm" />
                </div>
              </div>
            </div>

            <div style={{ display: enablePayment ? 'none' : 'block' }}>
              <button
                onClick={handlePaymentClick}
                className="w-full px-4 py-2 mt-4 text-white bg-red rounded border border-red hover:bg-white hover:text-red"
              >
                Proceed to Payment
              </button>
            </div>

            {popupCC && (
              <div
                id="overlay"
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
                onClick={handleClose}
              >
                <div
                  className="bg-white rounded-t-3xl sm:rounded-lg shadow-lg p-6 relative h-[95%] mt-[10%] sm:mt-[10px] w-full sm:h-auto sm:w-[450px] animate-slideUp"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Stripe
                    customerName={`${userInfo.name} ${userInfo.lastName}`}
                    total={(product.Price * quantity) + calculateDeliveryFee()}
                  />
                  <button
                    className="absolute top-[25px] left-[35px] text-gray-500 text-[20px] hover:text-gray-800"
                    onClick={() => setPopupCC(false)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded">
            <div className="border border-gray-200 rounded px-[30px] py-[10px] mb-[10px]">
              <p className="font-semibold text-[20px] mb-[10px]">Shipping Information:</p>
              <p className="text-[15px]">
                <b>Full Name</b>: {userInfo.name} {userInfo.lastName}
              </p>
              <p className="text-[15px]">
                <b>Email Address</b>: {userInfo.email}
              </p>
              <p className="text-[15px]">
                <b>Phone Number</b>: {userInfo.phone}
              </p>
              <p className="text-[15px]">
                <b>Address</b>: {userInfo.address}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <p className="text-[15px]">
                  <b>City</b>: {userInfo.city}
                </p>
                <p className="text-[15px]">
                  <b>State</b>: {userInfo.state}
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <p className="text-[15px]">
                  <b>Country</b>: {userInfo.country}
                </p>
                <p className="text-[15px]">
                  <b>Postal Code</b>: {userInfo.postalCode}
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded p-[20px]">
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={product.Image}
                  alt={product.Title}
                  className="w-20 h-20 object-contain"
                />
                <div>
                  <p className="font-semibold">{product.Title}</p>
                  <div className="flex items-center gap-2">
                    <label htmlFor="quantity" className="text-sm">Quantity:</label>
                    <select
                      id="quantity"
                      value={quantity}
                      onChange={(e) => {
                        const newQuantity = Number(e.target.value);
                        setQuantity(newQuantity);
                      }}
                      className="border border-gray-300 rounded px-2 py-1"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(product.Price * quantity)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between mb-2 text-green-600">
                    <span>Discount (10%):</span>
                    <span>-{formatCurrency(product.Price * quantity * discount)}</span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span>Shipping:</span>
                  <span>{getShippingDisplay()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-gray-200">
                  <span>Total:</span>
                  <span>{formatCurrency(totalCost)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomProductCheckout;
